import { type RefObject, type MutableRefObject, useLayoutEffect, useRef, useState } from 'react';
import type { FiltersDialogData } from 'components/core/FiltersDialog/types';

export const useFiltersResizeObserver = (
    containerRef: RefObject<HTMLDivElement>,
    filteredByRef: RefObject<HTMLDivElement>,
    tabsRef: RefObject<HTMLUListElement>,
    searchRef: RefObject<HTMLDivElement>,
    actionsRef: RefObject<HTMLDivElement>,
    selectedFilters: FiltersDialogData,
    searchQuery: string,
    filtersNaturalWidthRef: MutableRefObject<number>
) => {
    const [isFiltersWrapped, setIsFiltersWrapped] = useState(false);
    const isFiltersWrappedRef = useRef(false);

    useLayoutEffect(() => {
        const container = containerRef.current;
        const filteredBy = filteredByRef.current;
        const tabs = tabsRef.current;
        const search = searchRef.current;
        const actions = actionsRef.current;

        if (!container || !filteredBy || !tabs || !search || !actions) return undefined;

        const SEARCH_MAX_WIDTH = 400;
        const GAP = 16; // config.$grid * 2 = 8px * 2

        // When chips are on one line (nowrap), the inner <section> offsetWidth
        // is the true intrinsic content width. Store it so it stays correct
        // when we later switch to wrapped mode (where chips may reflow).
        if (!isFiltersWrappedRef.current) {
            const innerSection = filteredBy.firstElementChild?.firstElementChild as HTMLElement | null;
            if (innerSection) {
                // eslint-disable-next-line no-param-reassign
                filtersNaturalWidthRef.current = innerSection.offsetWidth;
            }
        }

        const checkWrap = () => {
            const containerWidth = container.clientWidth;
            const tabsWidth = tabs.offsetWidth;
            const searchWidth = Math.min(search.offsetWidth, SEARCH_MAX_WIDTH);
            const actionsWidth = actions.offsetWidth;

            // Space available for the filters section in a single-row layout:
            // container - tabs - search - actions - 3 gaps between the 4 columns
            const availableWidth = containerWidth - tabsWidth - searchWidth - actionsWidth - GAP * 3;

            const next = filtersNaturalWidthRef.current > availableWidth;
            if (next !== isFiltersWrappedRef.current) {
                isFiltersWrappedRef.current = next;
                setIsFiltersWrapped(next);
            }
        };

        if (typeof ResizeObserver === 'undefined') {
            checkWrap();
            return undefined;
        }

        const observer = new ResizeObserver(checkWrap);
        observer.observe(container);

        checkWrap();

        return () => observer.disconnect();
    }, [
        selectedFilters,
        searchQuery,
        containerRef,
        filteredByRef,
        tabsRef,
        searchRef,
        actionsRef,
        filtersNaturalWidthRef,
    ]);

    return { isFiltersWrapped };
};
